import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import ImageCropper from "../../components/ImageCropper/ImageCropper";
import ServiceUnavailableModal from "../../common/ServiceUnavailableModal/ServiceUnavailableModal";
import PublicPortalFaq from "../../components/PublicVerificationPortal/PublicPortalFaq";
import PublicPortalFooter from "../../components/PublicVerificationPortal/PublicPortalFooter";
import PublicPortalHeader from "../../components/PublicVerificationPortal/PublicPortalHeader";
import PublicPortalHero from "../../components/PublicVerificationPortal/PublicPortalHero";
import PublicUploadPreviewDialog from "../../components/PublicVerificationPortal/PublicUploadPreviewDialog";
import PublicVerificationDialog from "../../components/PublicVerificationPortal/PublicVerificationDialog";
import VerifiableCredentialsInfo from "../../components/PublicVerificationPortal/VerifiableCredentialsInfo";
import VerificationMethods from "../../components/PublicVerificationPortal/VerificationMethods";
import { portalConfig } from "./portalContent";
import { useTheme } from "../../store/ThemeContext";
import usePublicVerification, {
  normalizeBatchVerificationResponse,
} from "../../hooks/usePublicVerification";
import {
  PUBLIC_VERIFICATION_RESULT_KEY,
  clearUploadPreview,
  clearPublicVerificationSession,
  writeUploadPreview,
} from "../../utils/publicVerificationUploadStorage";
import { isCredentialSubjectEmpty } from "../../utils/vcVerificationDisplay";
import { getCameraErrorMessage } from "../../utils/cameraErrors";

const mapVerificationErrorMessage = (entry, t) => {
  const code = entry?.error_code;
  if (code) {
    const mapped = t(`publicPortal:verification.errorCodes.${code}`, {
      defaultValue: "",
    });
    if (mapped) return mapped;
  }
  return (
    entry?.message ||
    entry?.error ||
    t("publicPortal:verification.unexpectedError")
  );
};

const buildBatchErrorMessages = (errors, t) => {
  const messages = [];
  const seen = new Set();
  for (const entry of errors || []) {
    const message = mapVerificationErrorMessage(entry, t);
    if (!message || seen.has(message)) continue;
    seen.add(message);
    messages.push(message);
  }
  return messages;
};

const extractApiErrorMessage = (payload, t) => {
  if (!payload || typeof payload !== "object") return null;

  const entries = Array.isArray(payload.data) ? payload.data : [payload];

  for (const entry of entries) {
    if (!entry || typeof entry !== "object") continue;
    if (entry.error_code || entry.error || entry.message) {
      return mapVerificationErrorMessage(entry, t);
    }
  }

  if (payload.message || payload.detail) {
    return payload.message || payload.detail;
  }

  return null;
};

const PublicVerificationPortal = () => {
  const { t, i18n } = useTranslation(["publicPortal", "verify"]);
  const navigate = useNavigate();
  const theme = useTheme();
  const portalAssets = theme?.portalAssets || null;
  const isLoadingPortalAssets = theme?.isLoadingPortalAssets ?? false;
  const [activeMethod, setActiveMethod] = useState(null);
  const [isPreparingUpload, setIsPreparingUpload] = useState(false);
  const [methodResetKey, setMethodResetKey] = useState(0);
  const [pendingUpload, setPendingUpload] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImageSrc, setCropperImageSrc] = useState("");
  const [cropUploadMeta, setCropUploadMeta] = useState(null);
  const [retryAttempted, setRetryAttempted] = useState(false);
  const [showServiceUnavailableModal, setShowServiceUnavailableModal] =
    useState(false);
  const scanProcessingRef = useRef(false);
  const scanCaptchaTokenRef = useRef(null);
  const handleScanRef = useRef(null);
  const handleScanErrorRef = useRef(null);
  const {
    isVerifying,
    prepareCroppedFile,
    prepareFile,
    resetResult,
    verifyCredential,
    verifyCredentials,
    verifyScan,
  } = usePublicVerification();
  const captchaRequired = portalAssets?.verification_recaptcha_flag === true;
  const brand = t("publicPortal:brand", { returnObjects: true });
  const hero = t("publicPortal:hero", { returnObjects: true });
  const methods = portalConfig.methods.map((method) => ({
    ...method,
    ...t(`publicPortal:methods.${method.id}`, { returnObjects: true }),
  }));
  const contact = {
    ...portalConfig.contact,
    ...t("publicPortal:contact", { returnObjects: true }),
    email: portalAssets?.contact_email || portalConfig.contact.email,
    phone: portalAssets?.contact_number || portalConfig.contact.phone,
    address: portalAssets?.address || portalConfig.contact.address,
  };
  const footerText = t("publicPortal:footer", { returnObjects: true });
  const footer = {
    ...footerText,
    links:
      portalAssets?.products?.products?.map((product) => ({
        label: product.product_name,
        href: product.product_link,
      })) ||
      footerText.links.map((label, index) => ({
        label,
        href: portalConfig.footer.linkHrefs[index],
      })),
    documentation: footerText.documentation.map((label, index) => ({
      label,
      href: portalConfig.footer.documentationHrefs[index],
    })),
  };

  useEffect(() => {
    // Returning home ends the previous verification session (preview + results).
    clearPublicVerificationSession();
  }, []);

  useEffect(() => {
    return () => {
      window.__cameraPermissionPending = false;
    };
  }, []);

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    localStorage.setItem("selectedLanguage", language);
    document.documentElement.lang = language;
  };

  const getErrorMessage = async (error) => {
    if (error?.response) {
      try {
        const data = await error.response.json();
        return (
          extractApiErrorMessage(data, t) ||
          t("publicPortal:verification.unexpectedError")
        );
      } catch {
        // Fall through to the local error message.
      }
    }
    return error?.message || t("publicPortal:verification.unexpectedError");
  };

  const handleVerificationError = async (error) => {
    toast.error(await getErrorMessage(error));
  };

  const handleMethodSelect = (method, captchaToken = null) => {
    resetResult();
    if (method === "scan") {
      scanCaptchaTokenRef.current = captchaToken;
    }
    // Lets the handler in src/index.js tell an unanswered camera prompt apart from a
    // genuine permission denial. Cleared on any other method so it cannot leak into
    // unrelated permission flows.
    window.__cameraPermissionPending = method === "scan";
    setActiveMethod(method);
  };

  const resetVerificationMethodState = () => {
    window.__cameraPermissionPending = false;
    scanProcessingRef.current = false;
    scanCaptchaTokenRef.current = null;
    setMethodResetKey((current) => current + 1);
  };

  const clearCropperState = () => {
    setShowCropper(false);
    setCropperImageSrc("");
    setCropUploadMeta(null);
    setRetryAttempted(false);
  };

  const showVerificationResult = (dataOrResults, upload = null) => {
    resetVerificationMethodState();
    clearCropperState();
    setPendingUpload(null);
    setActiveMethod(null);

    const results = (
      Array.isArray(dataOrResults) ? dataOrResults : [dataOrResults]
    ).filter(
      (entry) => entry && !isCredentialSubjectEmpty(entry?.credential_subject),
    );

    if (results.length === 0) {
      clearUploadPreview();
      sessionStorage.removeItem(PUBLIC_VERIFICATION_RESULT_KEY);
      setShowServiceUnavailableModal(true);
      return;
    }

    if (upload?.document) {
      writeUploadPreview(upload);
    } else {
      clearUploadPreview();
    }

    sessionStorage.setItem(
      PUBLIC_VERIFICATION_RESULT_KEY,
      JSON.stringify(results),
    );
    navigate("/verification-result", {
      state: {
        results,
        result: results[0],
        upload: upload?.document
          ? {
              document: upload.document,
              fileName: upload.fileName || null,
              fileType: upload.fileType || null,
            }
          : null,
      },
    });
  };

  const applyVerificationOutcome = (
    { count, successes, errors },
    { upload = null, showBatchToast = false } = {},
  ) => {
    const errorMessages = buildBatchErrorMessages(errors, t);

    if (successes.length === 0) {
      if (errorMessages.length > 0) {
        toast.error(errorMessages.join(" "));
        resetVerificationMethodState();
        clearCropperState();
        setPendingUpload(null);
        setActiveMethod(null);
        return;
      }
      showVerificationResult([], upload);
      return;
    }

    if (showBatchToast) {
      const detected = t(
        "publicPortal:verification.batchDetectedValidQrCodes",
        {
          count,
        },
      );
      if (errorMessages.length > 0) {
        const verifiedPart = t(
          "publicPortal:verification.batchVerifiedSuccessfully",
          { count: successes.length },
        );
        const failedPart = t(
          "publicPortal:verification.batchFailedVerification",
          {
            count: errors.length,
            errorMessages: errorMessages.join(" "),
          },
        );
        toast.success(`${detected} ${verifiedPart} ${failedPart}`);
      } else {
        toast.success(detected);
      }
    } else if (errorMessages.length > 0) {
      toast.error(errorMessages.join(" "));
    }

    showVerificationResult(successes, upload);
  };

  const handleCredentialSubmit = async (credentialId, captchaToken = null) => {
    resetResult();
    setActiveMethod("credential");
    try {
      const trimmed = String(credentialId || "").trim();
      const normalizedCredentialId =
        trimmed.startsWith("credissuer:") ||
        trimmed.startsWith("did:") ||
        /^[a-f0-9]{64}$/i.test(trimmed)
          ? trimmed
          : `credissuer:${trimmed}`;
      const data = await verifyCredential({
        credentialId: normalizedCredentialId,
        captchaToken,
      });
      applyVerificationOutcome(normalizeBatchVerificationResponse(data, 1));
    } catch (error) {
      await handleVerificationError(error);
      resetVerificationMethodState();
      setActiveMethod(null);
    }
  };

  // Same stable-callback pattern as Validate.jsx so QrScannerView /
  // useDesktopPrintedQrBoost are not restarted on every parent re-render.
  const handleScan = async (detectedCodes) => {
    if (!detectedCodes?.[0]?.rawValue || scanProcessingRef.current) return;

    scanProcessingRef.current = true;
    try {
      const data = await verifyScan(detectedCodes, scanCaptchaTokenRef.current);
      scanCaptchaTokenRef.current = null;
      applyVerificationOutcome(normalizeBatchVerificationResponse(data, 1));
    } catch (error) {
      await handleVerificationError(error);
      resetVerificationMethodState();
      setActiveMethod(null);
    }
  };

  handleScanRef.current = handleScan;

  const stableHandleScan = useCallback((data) => {
    handleScanRef.current?.(data);
  }, []);

  // Without this a camera failure is indistinguishable from the scanner simply
  // finding nothing: a black box either way.
  const handleScanError = (error) => {
    console.error("Scanner error:", error);
    toast.error(getCameraErrorMessage(error, t));
    resetVerificationMethodState();
    setActiveMethod(null);
  };

  handleScanErrorRef.current = handleScanError;

  const stableHandleScanError = useCallback((error) => {
    handleScanErrorRef.current?.(error);
  }, []);

  const openCropper = (prepared) => {
    setCropUploadMeta({
      captchaToken: prepared.captchaToken,
      document: prepared.document,
      fileName: prepared.fileName,
      fileType: prepared.fileType,
    });
    setCropperImageSrc(prepared.cropperImageSrc);
    setShowCropper(true);
    setActiveMethod(null);
    setIsPreparingUpload(false);
  };

  const handleFileSelected = async (file, captchaToken) => {
    resetResult();
    clearCropperState();
    setActiveMethod("upload");
    setIsPreparingUpload(true);
    try {
      const preparedFile = await prepareFile(file, captchaToken);
      if (preparedFile.needsCrop) {
        openCropper(preparedFile);
        return;
      }
      setPendingUpload(preparedFile);
    } catch (error) {
      await handleVerificationError(error);
      resetVerificationMethodState();
      setActiveMethod(null);
    } finally {
      setIsPreparingUpload(false);
    }
  };

  const handleCroppedImage = async (croppedBlob) => {
    setShowCropper(false);
    setIsPreparingUpload(true);
    setActiveMethod("upload");
    try {
      const preparedFile = await prepareCroppedFile(
        croppedBlob,
        cropUploadMeta,
      );
      setPendingUpload(preparedFile);
      clearCropperState();
    } catch (error) {
      const message =
        error?.message || t("verify:unable_to_scan_selected_area");
      const canRetryCrop = message === t("verify:unable_to_scan_selected_area");

      toast.error(message);

      if (canRetryCrop) {
        setRetryAttempted(true);
        setShowCropper(true);
        setActiveMethod(null);
      } else {
        clearCropperState();
        resetVerificationMethodState();
        setActiveMethod(null);
      }
    } finally {
      setIsPreparingUpload(false);
    }
  };

  const handleCropperCancel = () => {
    if (!retryAttempted) {
      toast(t("verify:try_upload_again"));
    }
    clearCropperState();
    resetVerificationMethodState();
    setActiveMethod(null);
  };

  const confirmUploadVerification = async () => {
    if (!pendingUpload) return;
    const upload = pendingUpload;
    setPendingUpload(null);
    try {
      const credentialIds =
        upload.credentialIds?.length > 0
          ? upload.credentialIds
          : upload.credentialId
            ? [upload.credentialId]
            : [];

      const outcome = await verifyCredentials({
        credentialIds,
        captchaToken: upload.captchaToken,
      });

      applyVerificationOutcome(outcome, {
        upload,
        showBatchToast: true,
      });
    } catch (error) {
      await handleVerificationError(error);
      resetVerificationMethodState();
      setActiveMethod(null);
    }
  };

  const closeVerificationDialog = () => {
    resetVerificationMethodState();
    setPendingUpload(null);
    resetResult();
    setActiveMethod(null);
  };

  return (
    <div
      aria-busy={isLoadingPortalAssets}
      className="min-h-screen bg-cfSecondary text-cfHeadingText"
    >
      <PublicPortalHeader
        assets={portalAssets}
        brand={brand}
        isLoading={isLoadingPortalAssets}
        onLanguageChange={handleLanguageChange}
        showLanguageSwitch={
          Object.keys(portalAssets?.languages || {}).length > 1
        }
      />
      <main>
        <PublicPortalHero
          bannerImage={portalAssets?.banner_image}
          hero={hero}
          isLoading={isLoadingPortalAssets}
        />
        <VerificationMethods
          captchaRequired={captchaRequired}
          isLoading={isLoadingPortalAssets}
          methods={methods}
          onCredentialSubmit={handleCredentialSubmit}
          onFileSelected={handleFileSelected}
          onMethodSelect={handleMethodSelect}
          resetKey={methodResetKey}
        />
        <VerifiableCredentialsInfo
          content={t("publicPortal:credentialInfo", { returnObjects: true })}
        />
        <PublicPortalFaq
          contact={contact}
          faqs={t("publicPortal:faq.items", { returnObjects: true })}
          isLoading={isLoadingPortalAssets}
        />
      </main>
      <PublicPortalFooter
        assets={portalAssets}
        brand={brand}
        contact={contact}
        footer={footer}
        isLoading={isLoadingPortalAssets}
      />

      {showCropper && cropperImageSrc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
          <ImageCropper
            imageSrc={cropperImageSrc}
            onCrop={handleCroppedImage}
            onCancel={handleCropperCancel}
          />
        </div>
      )}

      {pendingUpload && (
        <PublicUploadPreviewDialog
          file={pendingUpload}
          onCancel={closeVerificationDialog}
          onConfirm={confirmUploadVerification}
        />
      )}

      {activeMethod && !pendingUpload && !showCropper && (
        <PublicVerificationDialog
          isLoading={isVerifying || isPreparingUpload}
          mode={activeMethod}
          onClose={closeVerificationDialog}
          onCredentialSubmit={handleCredentialSubmit}
          onScan={stableHandleScan}
          onScanError={stableHandleScanError}
          result={null}
        />
      )}

      <ServiceUnavailableModal
        isOpen={showServiceUnavailableModal}
        onClose={() => setShowServiceUnavailableModal(false)}
      />
    </div>
  );
};

export default PublicVerificationPortal;
